import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./NameSearch.css";
import { categoryLabel, safeScreeningSource, nameSearchDisplay } from "../utils/personScreening";

const API_URL = "https://www.lianfoong.com/api/name-search";
const abortError = () => Object.assign(new Error("Request cancelled"), { name: "AbortError" });
const pollDelay = (signal) => new Promise((resolve, reject) => {
  if (signal.aborted) { reject(abortError()); return; }
  const onAbort = () => { clearTimeout(timer); signal.removeEventListener("abort", onAbort); reject(abortError()); };
  const timer = setTimeout(() => { signal.removeEventListener("abort", onAbort); resolve(); }, 1500);
  signal.addEventListener("abort", onAbort, { once: true });
});
const statuses = ["POSSIBLE_MATCH", "NO_MATCH_FOUND", "UNABLE_TO_CHECK"];
const safeSource = safeScreeningSource;
const searchTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const parts = new Intl.DateTimeFormat("en-SG", {
    timeZone: "Asia/Singapore", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(date);
  const part = (type) => parts.find((item) => item.type === type)?.value || "";
  return `${part("day")}/${part("month")}/${part("year")} ${part("hour")}:${part("minute")}:${part("second")} SGT`;
};

export default function NameSearch() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const requestRef = useRef(null);
  const sequenceRef = useRef(0);
  const busyRef = useRef(false);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [localCheck, setLocalCheck] = useState(null);

  useEffect(() => {
    return () => {
      sequenceRef.current += 1;
      requestRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!loading) inputRef.current?.focus({ preventScroll: true });
  }, [loading]);

  const clear = () => {
    sequenceRef.current += 1;
    requestRef.current?.abort();
    busyRef.current = false;
    setLoading(false);
    setName("");
    setResult(null);
    setLocalCheck(null);
    inputRef.current?.focus();
  };

  const search = async (event) => {
    event.preventDefault();
    if (busyRef.current) return;
    const enteredName = name.trim().replace(/\s+/g, " ");
    if (!enteredName) {
      inputRef.current?.focus();
      return;
    }
    const sequence = ++sequenceRef.current;
    const controller = new AbortController();
    requestRef.current = controller;
    busyRef.current = true;
    setName(enteredName);
    setLoading(true);
    setResult(null);
    setLocalCheck(null);
    const timeout = setTimeout(() => controller.abort(), 150000);
    try {
      const read = async (url, options = {}) => {
        const response = await fetch(url, {
          cache: "no-store", signal: controller.signal, headers: { Accept: "application/json", ...options.headers }, ...options,
        });
        const data = await response.json().catch(() => null);
        if (sequence === sequenceRef.current && data?.localCheck) setLocalCheck(data.localCheck);
        if (!response.ok) throw new Error(data?.error || (response.status === 504
          ? "The server gateway timed out. Please try again."
          : `Name search failed (${response.status}). Please check that both backend and frontend updates were deployed.`));
        return { response, data };
      };
      let { response, data } = await read(`${API_URL}/jobs`, {
        method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ name: enteredName }),
      });
      if (response.status !== 202 || typeof data?.jobId !== "string" || !/^[0-9a-f-]{36}$/i.test(data.jobId)) {
        throw new Error("The search could not be started. Please deploy the matching backend update.");
      }
      const jobId = data.jobId;
      while (response.status === 202) {
        await pollDelay(controller.signal);
        if (sequence !== sequenceRef.current) return;
        ({ response, data } = await read(`${API_URL}/jobs/${encodeURIComponent(jobId)}`));
      }
      if (!data || !statuses.includes(data.status) || !statuses.includes(data.terrorismStatus) || !statuses.includes(data.pepStatus) || !Array.isArray(data.matches) || !Array.isArray(data.sources)) {
        throw new Error("An incomplete search result was returned. Please try again.");
      }
      if (sequence === sequenceRef.current) setResult(data);
    } catch {
      // Keep any independently completed ROP result, without displaying a search-failure message.
    } finally {
      clearTimeout(timeout);
      if (sequence === sequenceRef.current) {
        requestRef.current = null;
        busyRef.current = false;
        setLoading(false);
      }
    }
  };

  const display = result ? nameSearchDisplay(result, result.localCheck || localCheck) : null;
  return (
    <main className="name-search-page">
      <div className="ns-container">
        <header className="ns-header">
          <div className="ns-heading">
            <span className="ns-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5" />
              </svg>
            </span>
            <div><h1>Name Search</h1><p>Terrorist and PEP screening — uploaded ROP list first</p></div>
          </div>
          <button type="button" className="ns-button ns-button-secondary" onClick={() => navigate("/dashboard")}>← Back</button>
        </header>

        <section className="ns-card" aria-labelledby="name-search-label">
          <form onSubmit={search}>
            <label htmlFor="sales-search-name" id="name-search-label">Name to check</label>
            <p className="ns-help">Enter the person's name. Checks the uploaded ROP list by name only; no NRIC or birthdate is required.</p>
            <input ref={inputRef} id="sales-search-name" name="name" type="text" value={name}
              maxLength={180} autoComplete="off" spellCheck={false} disabled={loading}
              placeholder="Enter name"
              onChange={(event) => { setName(event.target.value); setResult(null); setLocalCheck(null); }} />
            <div className="ns-actions">
              <button type="submit" className="ns-button ns-button-primary" disabled={loading || !name.trim()}>
                {loading ? "Searching…" : "Check Name"}
              </button>
              <button type="button" className="ns-button ns-button-secondary" onClick={clear}>Clear</button>
            </div>
          </form>
          {loading && <div className="ns-searching" role="status"><span className="ns-spinner" aria-hidden="true" /> Checking the uploaded ROP list, then terrorist and PEP web evidence. Please wait…</div>}
        </section>

        {localCheck && ["NO_MATCH_FOUND", "POSSIBLE_MATCH"].includes(localCheck.status) && (
          <section className="ns-card" aria-labelledby="rop-check-heading">
            <h3 id="rop-check-heading">Uploaded ROP list — name-only check</h3>
            <p className="ns-summary" role="status">
              {localCheck.status === "POSSIBLE_MATCH"
                ? `${localCheck.matchCount} possible name match${localCheck.matchCount === 1 ? "" : "es"} found. Review the matching details.`
                : "Cleared — no potential name match in the uploaded ROP terrorist list."}
            </p>
            <p className="ns-help">
              {Number.isFinite(localCheck.listCount) && `${localCheck.listCount} list entries checked. `}
              {localCheck.lastUpdatedAt && `Last upload: ${searchTime(localCheck.lastUpdatedAt)}. `}
              Birthdate was not used for matching.
            </p>
          </section>
        )}

        {result && display.checks.length > 0 && (
          <section className={`ns-card ns-result ns-result-${display.kind}`} aria-labelledby="search-result-heading">
            <div className="ns-result-header">
              <span className="ns-result-label" id="search-result-heading" role="status">{display.label}</span>
              <span className="ns-time">{searchTime(result.searchedAt)}</span>
            </div>
            <div className="ns-checks">
              {display.checks.map(check => <div className={`ns-check ns-check-${check.kind}`} key={check.title}>
                <h3>{check.title}</h3><strong>{check.label}</strong><p>{check.detail}</p>
              </div>)}
            </div>
            <h2>{result.name}</h2>
            <p className="ns-summary">{display.summary}</p>
            {display.kind === "match" && <p className="ns-review">A matching name does not confirm this person's identity. Compare the published details before taking action.</p>}
            {result.matches.map((match, index) => (
              <article className="ns-match" key={`${match.reference || match.listedName}-${index}`}>
                <h3>{match.listedName}</h3>
                <dl>
                  <div><dt>Category</dt><dd>{categoryLabel(match.category)}</dd></div>
                  <div><dt>List / source</dt><dd>{match.listName}</dd></div>
                  {match.role && <div><dt>Public role</dt><dd>{match.role}</dd></div>}
                  {match.country && <div><dt>Country / organisation</dt><dd>{match.country}</dd></div>}
                  {match.relationship && <div><dt>PEP relationship</dt><dd>{match.relationship}</dd></div>}
                  {match.reference && <div><dt>Reference</dt><dd>{match.reference}</dd></div>}
                  {match.birthdate && <div><dt>Published birthdate</dt><dd>{match.birthdate}</dd></div>}
                  {match.nationality && <div><dt>Published nationality</dt><dd>{match.nationality}</dd></div>}
                  {match.passportNo && <div><dt>Listed passport no.</dt><dd>{match.passportNo}</dd></div>}
                </dl>
                <p>{match.reason}</p>
                {safeSource(match.sourceUrl) && <a href={match.sourceUrl} target="_blank" rel="noopener noreferrer">View supporting source ↗</a>}
              </article>
            ))}
            {display.onlineResultsAvailable && result.sources.length > 0 && <div className="ns-sources"><h3>Sources consulted</h3>
              <ul>{result.sources.filter((source) => safeSource(source.url)).map((source) => (
                <li key={source.url}><a href={source.url} target="_blank" rel="noopener noreferrer">{source.title || new URL(source.url).hostname} ↗</a></li>
              ))}</ul>
            </div>}
          </section>
        )}
        <p className="ns-notice">Each result applies to its named check. Cleared means no potential match in that list or the online sources checked. PEP status does not imply wrongdoing. Compare identifying details when reviewing a potential match.</p>
      </div>
    </main>
  );
}
